<template>
    <div class="page-container">
        <div class="settings-form">
            <h2 class="page-title">云存储配置</h2>

            <!-- 云服务商选择 -->
            <div class="cloud-provider-selector">
                <el-radio-group v-model="activeTab" class="tab-selector">
                    <el-radio-button label="aliyun">阿里云OSS</el-radio-button>
                    <el-radio-button label="tencent">腾讯云COS</el-radio-button>
                    <el-radio-button label="qiniu">七牛云OSS</el-radio-button>
                </el-radio-group>

                <!-- 默认云服务商设置 -->
                <div class="default-provider">
                    <span class="label">默认云服务商：</span>
                    <el-select v-model="defaultProvider" style="width: 200px;" placeholder="选择默认云服务商"
                        @change="updateDefaultProvider">
                        <el-option label="阿里云OSS" value="aliyun" />
                        <el-option label="腾讯云COS" value="tencent" />
                        <el-option label="七牛云OSS" value="qiniu" />
                    </el-select>
                    <el-tooltip content="当商户未配置具体云服务商时，将使用此默认设置" placement="top">
                        <el-icon class="help-icon"><info-filled /></el-icon>
                    </el-tooltip>

                    <!-- 如果当前是local，显示提示信息 -->
                    <div v-if="actualProvider === 'local'" class="local-provider-tip">
                        <el-tag type="info">当前使用本地存储</el-tag>
                    </div>
                </div>
            </div>

            <!-- 阿里云OSS配置 -->
            <div v-show="activeTab === 'aliyun'" class="tab-content">
                <el-form :model="aliyunForm" label-width="140px" class="compact-form">
                    <el-form-item label="阿里云ID：" class="form-item-row">
                        <el-input v-model="aliyunForm.aliyun_keyid" placeholder="请输入阿里云AccessKey ID" size="large"
                            show-password clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <key />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="阿里云密钥：" class="form-item-row">
                        <el-input v-model="aliyunForm.aliyun_secret" placeholder="请输入阿里云AccessKey Secret" size="large"
                            show-password clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <lock />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="Endpoint：" class="form-item-row">
                        <el-input v-model="aliyunForm.endpoint" placeholder="例如：oss-cn-hangzhou.aliyuncs.com"
                            size="large" clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <location />
                                </el-icon>
                            </template>
                        </el-input>
                        <div class="cloud-tip">
                            <el-icon class="tip-icon"><info-filled /></el-icon>
                            <span>Bucket所在区域的外网Endpoint</span>
                        </div>
                    </el-form-item>

                    <el-form-item label="Bucket名称：" class="form-item-row">
                        <el-input v-model="aliyunForm.aliyun_bucket" placeholder="请输入Bucket名称" size="large" clearable
                            class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <box />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="连接测试：" class="form-item-row">
                        <el-button type="info" size="large" class="test-btn" @click="testConnection('aliyun')"
                            :loading="testing.aliyun">
                            <el-icon class="btn-icon">
                                <connection />
                            </el-icon>
                            测试OSS连接
                        </el-button>
                        <TestResult :result="testResult.aliyun" :message="testMessage.aliyun" />
                    </el-form-item>

                    <FormActions @submit="submitConfig('aliyun')" @reset="resetConfig" :loading="loading.aliyun"
                        :cloud-type="'aliyun'" />
                </el-form>
            </div>

            <!-- 腾讯云COS配置 -->
            <div v-show="activeTab === 'tencent'" class="tab-content">
                <el-form :model="tencentForm" label-width="140px" class="compact-form">
                    <el-form-item label="SecretId：" class="form-item-row">
                        <el-input v-model="tencentForm.tencent_secret_id" placeholder="请输入腾讯云SecretId" size="large"
                            show-password clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <key />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="SecretKey：" class="form-item-row">
                        <el-input v-model="tencentForm.tencent_secret_key" placeholder="请输入腾讯云SecretKey" size="large"
                            show-password clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <lock />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="Bucket名称：" class="form-item-row">
                        <el-input v-model="tencentForm.tencent_bucket" placeholder="请输入Bucket名称" size="large" clearable
                            class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <box />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="地域Region：" class="form-item-row">
                        <el-input v-model="tencentForm.tencent_region" placeholder="例如：ap-beijing" size="large"
                            clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <location />
                                </el-icon>
                            </template>
                        </el-input>
                        <div class="cloud-tip">
                            <el-icon class="tip-icon"><info-filled /></el-icon>
                            <span>Bucket所在的地域代码，如ap-beijing（北京）</span>
                        </div>
                    </el-form-item>

                    <el-form-item label="连接测试：" class="form-item-row">
                        <el-button type="info" size="large" class="test-btn" @click="testConnection('tencent')"
                            :loading="testing.tencent">
                            <el-icon class="btn-icon">
                                <connection />
                            </el-icon>
                            测试COS连接
                        </el-button>
                        <TestResult :result="testResult.tencent" :message="testMessage.tencent" />
                    </el-form-item>

                    <FormActions @submit="submitConfig('tencent')" @reset="resetConfig" :loading="loading.tencent"
                        :cloud-type="'tencent'" />
                </el-form>
            </div>

            <!-- 七牛云配置 -->
            <div v-show="activeTab === 'qiniu'" class="tab-content">
                <el-form :model="qiniuForm" label-width="140px" class="compact-form">
                    <el-form-item label="AccessKey：" class="form-item-row">
                        <el-input v-model="qiniuForm.qiniu_access_key" placeholder="请输入七牛云AccessKey" size="large"
                            show-password clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <key />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="SecretKey：" class="form-item-row">
                        <el-input v-model="qiniuForm.qiniu_secret_key" placeholder="请输入七牛云SecretKey" size="large"
                            show-password clearable class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <lock />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="Bucket名称：" class="form-item-row">
                        <el-input v-model="qiniuForm.qiniu_bucket" placeholder="请输入Bucket名称" size="large" clearable
                            class="cloud-input">
                            <template #prefix>
                                <el-icon>
                                    <box />
                                </el-icon>
                            </template>
                        </el-input>
                    </el-form-item>

                    <el-form-item label="存储区域：" class="form-item-row">
                        <el-select v-model="qiniuForm.qiniu_region" placeholder="请选择存储区域" class="cloud-input">
                            <el-option label="华东（z0）" value="z0" />
                            <el-option label="华北（z1）" value="z1" />
                            <el-option label="华南（z2）" value="z2" />
                            <el-option label="北美（na0）" value="na0" />
                            <el-option label="东南亚（as0）" value="as0" />
                        </el-select>
                        <div class="cloud-tip">
                            <el-icon class="tip-icon"><info-filled /></el-icon>
                            <span>选择Bucket所在的存储区域</span>
                        </div>
                    </el-form-item>

                    <el-form-item label="域名Domain：" class="form-item-row">
                        <el-input v-model="qiniuForm.qiniu_domain" placeholder="例如：https://xxx.bkt.clouddn.com"
                            size="large" clearable class="cloud-input">

                        </el-input>
                        <div class="cloud-tip">
                            <el-icon class="tip-icon"><info-filled /></el-icon>
                            <span>文件访问域名，可在七牛云控制台获取</span>
                        </div>
                    </el-form-item>

                    <el-form-item label="连接测试：" class="form-item-row">
                        <el-button type="info" size="large" class="test-btn" @click="testConnection('qiniu')"
                            :loading="testing.qiniu">
                            <el-icon class="btn-icon">
                                <connection />
                            </el-icon>
                            测试七牛云连接
                        </el-button>
                        <TestResult :result="testResult.qiniu" :message="testMessage.qiniu" />
                    </el-form-item>

                    <FormActions @submit="submitConfig('qiniu')" @reset="resetConfig" :loading="loading.qiniu"
                        :cloud-type="'qiniu'" />
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Key, Lock, Location, Box, Connection,
    InfoFilled
} from '@element-plus/icons-vue'
import TestResult from './TestResult.vue';
import FormActions from './FormActions.vue';

export default {
    name: 'SystemOssConfig',
    components: {
        Key, Lock, Location, Box, Connection,
        InfoFilled, TestResult, FormActions
    },
    data() {
        return {
            activeTab: 'aliyun',
            defaultProvider: 'aliyun',
            actualProvider: 'aliyun',   // 实际的后端值（可能包含local）

            loading: {
                aliyun: false,
                tencent: false,
                qiniu: false
            },
            testing: {
                aliyun: false,
                tencent: false,
                qiniu: false
            },
            testResult: {
                aliyun: null,
                tencent: null,
                qiniu: null
            },
            testMessage: {
                aliyun: '',
                tencent: '',
                qiniu: ''
            },
            aliyunForm: {
                aliyun_keyid: '',
                aliyun_secret: '',
                endpoint: '',
                aliyun_bucket: ''
            },
            tencentForm: {
                tencent_secret_id: '',
                tencent_secret_key: '',
                tencent_bucket: '',
                tencent_region: ''
            },
            qiniuForm: {
                qiniu_access_key: '',
                qiniu_secret_key: '',
                qiniu_bucket: '',
                qiniu_region: 'z0',
                qiniu_domain: ''
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.fetchAllConfig()
    },
    methods: {
        async fetchAllConfig() {
            try {
                // 获取默认云服务商
                const providerRes = await axios.post(`${config.BASE_URL}/shop/api/LaBasicSettingsController.php`, {
                    action: 'get_cloud_provider',
                    store_id: this.userInfo.id
                })
                if (providerRes.data.data) {
                    const backendProvider = providerRes.data.data.cloud_provider || 'aliyun'
                    this.actualProvider = backendProvider

                    // 如果后端返回的是local，下拉框显示aliyun，但实际值保持local
                    if (backendProvider === 'local') {
                        this.defaultProvider = 'aliyun' // 或者其他默认值
                    } else {
                        this.defaultProvider = backendProvider
                    }
                }

                // 获取阿里云配置
                const aliyunRes = await axios.post(`${config.BASE_URL}/shop/api/LaBasicSettingsController.php`, {
                    action: 'get_oss',
                    store_id: this.userInfo.id
                })
                if (aliyunRes.data.data) {
                    this.aliyunForm = { ...this.aliyunForm, ...aliyunRes.data.data }
                }

                // 获取腾讯云配置
                const tencentRes = await axios.post(`${config.BASE_URL}/shop/api/LaBasicSettingsController.php`, {
                    action: 'get_tencent',
                    store_id: this.userInfo.id
                })
                if (tencentRes.data.data) {
                    this.tencentForm = { ...this.tencentForm, ...tencentRes.data.data }
                }

                // 获取七牛云配置
                const qiniuRes = await axios.post(`${config.BASE_URL}/shop/api/LaBasicSettingsController.php`, {
                    action: 'get_qiniu',
                    store_id: this.userInfo.id
                })
                if (qiniuRes.data.data) {
                    this.qiniuForm = { ...this.qiniuForm, ...qiniuRes.data.data }
                }
            } catch (error) {
                this.$message.error('获取配置失败')
                console.error('获取配置错误:', error)
            }
        },

        async updateDefaultProvider() {
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/LaBasicSettingsController.php`, {
                    action: 'update_cloud_provider',
                    store_id: this.userInfo.id,
                    cloud_provider: this.defaultProvider
                })

                if (data.success) {
                    this.actualProvider = this.defaultProvider

                    this.$message.success('默认云服务商设置成功')
                } else {
                    this.$message.error(data.error || '设置失败')
                }
            } catch (error) {
                this.$message.error('设置失败: ' + (error.response?.data?.error || error.message))
            }
        },

        async testConnection(cloudType) {
            const form = this.getFormByType(cloudType)
            const requiredFields = this.getRequiredFields(cloudType)

            for (const field of requiredFields) {
                if (!form[field]) {
                    this.$message.warning('请填写完整的配置信息')
                    return
                }
            }

            this.testing[cloudType] = true
            this.testResult[cloudType] = null
            this.testMessage[cloudType] = ''

            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/test_oss_connection.php`, {
                    cloud_type: cloudType,
                    ...form,
                    store_id: this.userInfo.id
                })

                this.testResult[cloudType] = data.success
                this.testMessage[cloudType] = data.message || (data.success ? '连接测试成功' : '连接测试失败')

                if (data.success) {
                    this.$message.success(`${this.getCloudName(cloudType)}连接测试成功`)
                } else {
                    this.$message.error(`${this.getCloudName(cloudType)}连接测试失败: ${data.error || '未知错误'}`)
                }
            } catch (error) {
                this.testResult[cloudType] = false
                this.testMessage[cloudType] = error.response?.data?.error || error.message || '测试连接时发生错误'
                this.$message.error(`测试连接失败: ${this.testMessage[cloudType]}`)
            } finally {
                this.testing[cloudType] = false
            }
        },

        async submitConfig(cloudType) {
            const form = this.getFormByType(cloudType)
            const requiredFields = this.getRequiredFields(cloudType)

            for (const field of requiredFields) {
                if (!form[field]) {
                    this.$message.warning('请填写完整的配置信息')
                    return
                }
            }

            this.loading[cloudType] = true
            try {
                const actionMap = {
                    aliyun: 'update_oss',
                    tencent: 'update_tencent',
                    qiniu: 'update_qiniu'
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/LaBasicSettingsController.php`,
                    {
                        action: actionMap[cloudType],
                        store_id: this.userInfo.id,
                        ...form
                    }
                )

                if (data.success) {
                    this.$message.success(`${this.getCloudName(cloudType)}配置保存成功`)
                } else {
                    this.$message.error(data.error || '保存失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '服务器错误'
                this.$message.error(`保存失败：${errorMsg}`)
            } finally {
                this.loading[cloudType] = false
            }
        },

        async resetConfig(cloudType, resetType) {
            if (resetType === 'clear') {
                // 清空表单
                const emptyForm = this.getEmptyForm(cloudType)
                Object.assign(this.getFormByType(cloudType), emptyForm)
                this.testResult[cloudType] = null
                this.testMessage[cloudType] = ''
                this.$message.success('表单已清空')
            } else {
                // 恢复默认（清空数据库配置）
                try {
                    const actionMap = {
                        aliyun: 'clear_oss',
                        tencent: 'clear_tencent',
                        qiniu: 'clear_qiniu'
                    }

                    const { data } = await axios.post(
                        `${config.BASE_URL}/shop/api/LaBasicSettingsController.php`,
                        {
                            action: actionMap[cloudType],
                            store_id: this.userInfo.id
                        }
                    )

                    if (data.success) {
                        this.$message.success('配置已重置为默认值')
                        this.fetchAllConfig() // 重新加载
                    } else {
                        this.$message.error(data.error || '重置失败')
                    }
                } catch (error) {
                    this.$message.error('重置失败: ' + (error.response?.data?.error || error.message))
                }
            }
        },

        // 辅助方法
        getFormByType(cloudType) {
            const formMap = {
                aliyun: this.aliyunForm,
                tencent: this.tencentForm,
                qiniu: this.qiniuForm
            }
            return formMap[cloudType]
        },

        getRequiredFields(cloudType) {
            const fieldMap = {
                aliyun: ['aliyun_keyid', 'aliyun_secret', 'endpoint', 'aliyun_bucket'],
                tencent: ['tencent_secret_id', 'tencent_secret_key', 'tencent_bucket', 'tencent_region'],
                qiniu: ['qiniu_access_key', 'qiniu_secret_key', 'qiniu_bucket']
            }
            return fieldMap[cloudType]
        },

        getCloudName(cloudType) {
            const nameMap = {
                aliyun: '阿里云OSS',
                tencent: '腾讯云COS',
                qiniu: '七牛云OSS'
            }
            return nameMap[cloudType]
        },

        getEmptyForm(cloudType) {
            const emptyForms = {
                aliyun: { aliyun_keyid: '', aliyun_secret: '', endpoint: '', aliyun_bucket: '' },
                tencent: { tencent_secret_id: '', tencent_secret_key: '', tencent_bucket: '', tencent_region: '' },
                qiniu: { qiniu_access_key: '', qiniu_secret_key: '', qiniu_bucket: '', qiniu_region: 'z0', qiniu_domain: '' }
            }
            return emptyForms[cloudType]
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.cloud-provider-selector {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;

    .tab-selector {
        :deep(.el-radio-button__inner) {
            padding: 12px 24px;
            font-size: 14px;
        }
    }

    .default-provider {
        display: flex;
        align-items: center;
        gap: 12px;

        .label {
            font-size: 14px;
            color: #374151;
            white-space: nowrap;
        }

        .help-icon {
            color: #6b7280;
            cursor: help;
        }
    }
}

.tab-content {
    margin-top: 20px;
}

.compact-form {
    :deep(.el-form-item__label) {
        font-size: 16px;
        color: #374151;
        line-height: 40px;
        padding-right: 24px !important;
    }

    .form-item-row {
        margin-bottom: 28px;

        :deep(.el-form-item__content) {
            flex: 1;
            min-width: 0;
        }
    }
}

.cloud-input {
    width: 100%;
    max-width: 500px;
}

.cloud-tip {
    margin-top: 8px;
    margin-left: 10px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
    display: flex;
    align-items: center;

    .tip-icon {
        color: #3b82f6;
        margin-right: 8px;
        font-size: 16px;
    }
}

.test-btn {
    width: 200px;
    padding: 14px 24px;

    .btn-icon {
        margin-right: 8px;
        font-size: 16px;
    }
}

.test-result {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 12px;

    .result-message {
        color: #64748b;
        font-size: 13px;
    }
}

.form-actions {
    margin-top: 40px;
    text-align: center;

    .submit-btn,
    .reset-btn {
        width: 160px;
        padding: 14px 24px;
        font-size: 15px;
        letter-spacing: 0.5px;

        .btn-icon {
            margin-right: 8px;
            font-size: 16px;
        }
    }
}

.local-provider-tip {
    margin-left: 12px;
}
</style>